<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePremiumStore } from '@state/stores/premium'
import localSave from './../../../utils/local-save'
import { trialDays } from './../../../configuration'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import {
    WALL_TYPES,
    PRESENTATION_TYPES,
    TRIGGER_REASONS,
    ENTRY_POINTS,
    CTA_TYPES,
    DISMISS_METHODS
} from '@services/mixpanel/analytics-spec'

const route = useRoute()
const router = useRouter()
const premiumStore = usePremiumStore()

const entitledFreeTrial = computed(() => !premiumStore.hasEverBeenPremium)

function getEntryPoint() {
    const query = route.query
    if (query.from === 'topbar') return ENTRY_POINTS.TOP_BAR
    if (query.from === 'sidebar') return ENTRY_POINTS.SIDEBAR
    if (query.from === 'settings') return ENTRY_POINTS.SETTINGS_AUDIO_QUALITY
    if (query.from === 'account') return ENTRY_POINTS.ACCOUNT_MENU
    return ENTRY_POINTS.DIRECT_NAVIGATION
}

function onUpgradeClick() {
    paywallTracker.onCtaTapped(
        entitledFreeTrial.value ? CTA_TYPES.START_TRIAL : CTA_TYPES.UPGRADE
    )
    router.push(route.path + '/view-plans')
}

function skipPremiumOnboarding() {
    paywallTracker.onWallDismissed(DISMISS_METHODS.CLOSE)
    localSave.save('hasSkippedPremiumOnboarding', true)
    if (route.path !== '/') {
        router.back()
    } else {
        router.push({ path: '/' })
    }
}

onMounted(() => {
    paywallTracker.onWallShown({
        wallType: WALL_TYPES.PREMIUM_WELCOME,
        presentation: PRESENTATION_TYPES.PAGE,
        triggerReason: TRIGGER_REASONS.USER_INITIATED,
        entryPoint: getEntryPoint(),
    })
})
</script>

<template>
    <div class="welcome">
        <div class="welcome-background" />
        <BaseSection padded>
            <h1 class="main-header">Go Premium</h1>
            <div class="description">
                <p class="description-text">{{ entitledFreeTrial ? 'Unlock Hi-Fi sound, zero ads and offline mixes - free for your first month.' : 'Unlock Hi-Fi sound, zero ads and offline mixes.' }}</p>
                <ul class="sales-points">
                    <li>Hi-Fi sound</li>
                    <li>No ads</li>
                    <li>Stream radio channels in external players and devices</li>
                    <li>Download mixes & listen offline anywhere on our mobile apps</li>
                    <li>VIP access: Skip the line and access new mixes before everyone else</li>
                </ul>
            </div>
            <div class="actions-container">
                <div>
                    <BaseButton
                        button-type="highlight"
                        @click="onUpgradeClick"
                    > {{ entitledFreeTrial ? `Start ${trialDays}-Day Free Trial` : 'Upgrade Now' }} </BaseButton>
                </div>
                <span
                    class="escape-url"
                    @click="skipPremiumOnboarding"
                ><div>Maybe later - I like ads and low quality sound</div></span>
            </div>
        </BaseSection>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.welcome {
    margin-top: $s-xs;
}

.welcome-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: url('/images/dj-vibing-purple-gradient.jpg');
    background-position: center;
    background-size: cover;
    opacity: 0.08;

    &.standalone {
        position: fixed;
    }
}

.main-header {
    @include h3;

    margin-bottom: 0;  // Remove all bottom margin
    letter-spacing: -0.5px;
}

.description {
    margin-top: 0;
}

.actions-container {
    position: relative;
    margin-top: $s-xxl;
}

.sales-points {
    @extend %contentSubtitleL;

    padding: 0;
    margin-top: $s-xl;
    font-size: 15px;
    line-height: 20px;
    text-align: left;
    list-style: inside;
}

.escape-url {
    @extend %contentSubtitleM;

    text-align: center;
    text-decoration: underline;
    cursor: pointer;
}

.description-text {
    @extend %h6;

    font-weight: 400;
    color: $c-nevada-grey;
}

@include tablet {
    .actions-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .escape-url {
        text-align: left;
    }
}

@include desktop {
    .welcome {
        margin-top: $s-h;
    }

    .main-header {
        @include h1;

        margin-bottom: $s-xxl;
    }

    .actions-container {
        margin-top: $s-h;
    }
}
</style>
